import { intervalToDuration } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type TimeComponent = {
  value: number;
  label: string;
  unit: string;
};

type UseAdditionalPayoutCountdownWidgetProps = {
  isEnabled: boolean;
  deadline: Date;
};

type UseAdditionalPayoutCountdownWidgetReturn = {
  timeComponents: TimeComponent[];
  hasDeadlinePassed: boolean;
  isEnabled: boolean;
};

function useAdditionalPayoutCountdownWidget({
  isEnabled,
  deadline,
}: UseAdditionalPayoutCountdownWidgetProps): UseAdditionalPayoutCountdownWidgetReturn {
  const { t } = useTranslation('contest');
  const [timeComponents, setTimeComponents] = useState<TimeComponent[]>([]);
  const [hasDeadlinePassed, setHasDeadlinePassed] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const targetDate = useMemo(() => (deadline ? new Date(deadline) : null), [deadline]);

  const updateCountdown = useCallback(() => {
    if (!targetDate) return;

    const now = new Date();

    if (now >= targetDate) {
      if (!hasDeadlinePassed) {
        setHasDeadlinePassed(true);
        setTimeComponents([]);
      }
      return;
    }

    const duration = intervalToDuration({ start: now, end: targetDate });

    const components: TimeComponent[] = [];

    // Days
    if (duration.days && duration.days > 0) {
      components.push({
        value: duration.days,
        label: t('common.additionalPayoutCountdownWidget.time.day__hasPlural', {
          count: duration.days,
        }),
        unit: 'days',
      });
    }

    // Hours
    if (duration.hours && duration.hours > 0) {
      components.push({
        value: duration.hours,
        label: t('common.additionalPayoutCountdownWidget.time.hour__hasPlural', {
          count: duration.hours,
        }),
        unit: 'hours',
      });
    }

    // Minutes
    if (duration.minutes && duration.minutes > 0) {
      components.push({
        value: duration.minutes,
        label: t('common.additionalPayoutCountdownWidget.time.minute__hasPlural', {
          count: duration.minutes,
        }),
        unit: 'minutes',
      });
    }

    // If we're down to seconds only, show minutes as 0
    if (components.length === 0) {
      components.push({
        value: 0,
        label: t('common.additionalPayoutCountdownWidget.time.minute__hasPlural', { count: 0 }),
        unit: 'minutes',
      });
    }

    // Only update state if components have actually changed
    setTimeComponents((prevComponents) => {
      const hasChanged =
        prevComponents.length !== components.length ||
        prevComponents.some(
          (prev, index) =>
            prev.value !== components[index]?.value || prev.unit !== components[index]?.unit
        );

      return hasChanged ? components : prevComponents;
    });
  }, [targetDate, hasDeadlinePassed, t]);

  useEffect(() => {
    if (!isEnabled || !targetDate) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (new Date() >= targetDate) {
      setHasDeadlinePassed(true);
      setTimeComponents([]);
      return;
    }

    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isEnabled, targetDate, updateCountdown]);

  return {
    timeComponents,
    hasDeadlinePassed,
    isEnabled: Boolean(isEnabled),
  };
}

export default useAdditionalPayoutCountdownWidget;
